'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FinancialModel } from '@/lib/financial-model';

interface Props {
  scenarioModels: Array<{ key: string; model: FinancialModel }>;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload.map((entry, idx) => (
        <div key={`sc-chart-tt-${idx}`} className="flex items-center gap-2 mb-1">
          <span
            className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-mono font-semibold text-foreground">
            ₹{entry.value.toFixed(2)} Cr
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ScenarioComparisonChart({ scenarioModels }: Props) {
  const data = [
    { metric: 'Revenue (Yr2)' },
    { metric: 'Gross Profit (Yr2)' },
    { metric: 'EBITDA (Yr2)' },
    { metric: 'PAT (Yr2)' },
  ].map((row) => {
    const out: Record<string, string | number> = { metric: row.metric };
    scenarioModels.forEach(({ key, model }) => {
      const yr2 = model.years[2];
      if (!yr2) return;
      if (row.metric.startsWith('Revenue'))
        out[key] = parseFloat((yr2.revenue / 10000000).toFixed(2));
      else if (row.metric.startsWith('Gross'))
        out[key] = parseFloat((yr2.grossProfit / 10000000).toFixed(2));
      else if (row.metric.startsWith('EBITDA'))
        out[key] = parseFloat((yr2.ebitda / 10000000).toFixed(2));
      else out[key] = parseFloat((yr2.pat / 10000000).toFixed(2));
    });
    return out;
  });

  const COLORS: Record<string, string> = {
    downside: 'var(--danger)',
    base: 'var(--primary)',
    upside: 'var(--accent)',
  };

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }} barGap={4}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="metric"
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Sans' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `₹${v}Cr`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }}
          iconType="square"
          iconSize={10}
        />
        {scenarioModels.map(({ key }) => (
          <Bar
            key={`scenario-bar-${key}`}
            dataKey={key}
            name={key.charAt(0).toUpperCase() + key.slice(1)}
            fill={COLORS[key]}
            radius={[3, 3, 0, 0]}
            maxBarSize={36}
            opacity={0.85}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
